import axios from 'axios'
import queryString from 'query-string'

import agent from 'agent'
import { getBaseUrl } from 'utils/baseUrls'

const {
  REACT_APP_GOOGLE_OAUTH_CLIENT_ID,
  REACT_APP_GOOGLE_OAUTH_SCOPE,
  REACT_APP_GOOGLE_OAUTH_URL,
  REACT_APP_GOOGLE_OAUTH_TOKENINFO_URL
} = process.env

const urlEncodedHeaders = {
  'Content-Type': 'application/x-www-form-urlencoded'
}

export default {
  signin({ email, password, companyName, token }) {
    const body = new URLSearchParams()

    body.append('email', email)
    body.append('password', password)

    // Company name is not available for new users on initial login
    if (companyName) body.append('companyName', companyName)

    let url = '/users/auth?getCompanySession=true'

    // If a token is provided the URL and body change
    if (token) {
      body.append('token', token)
      url = 'users/auth/mfa/auth?getCompanySession=true'
    }

    return agent.post(url, body).then(({ data }) => data)
  },

  renew(session) {
    return Promise.all(
      session.teams.map(credential =>
        agent
          .post(
            '/users/renew',
            queryString.stringify({
              email: credential.identifier,
              orgId: credential.org_id,
              renewToken: credential.renew_token
            }),
            { headers: urlEncodedHeaders }
          )
          .then(({ data }) => ({ ...credential, ...data }))
      )
    )
  },

  signinWithGoogle({ accessToken, companyName }) {
    const headers = {
      'Content-Type': 'application/x-www-form-urlencoded'
    }

    const parseGoogleResponse = ({ data }) => {
      if (!data || !data.aud) return Promise.reject('invalid-token')
      return data.aud === REACT_APP_GOOGLE_OAUTH_CLIENT_ID
        ? Promise.resolve(data)
        : Promise.reject('invalid-token')
    }

    const apiServerPromise = ({ email }) =>
      agent
        .post(
          '/users/auth/sso?getCompanySession=true',
          queryString.stringify({
            email,
            accessToken,
            provider: 'google',
            ...(companyName && { companyName })
          }),
          headers
        )
        .then(({ data }) => data)

    return axios
      .get(REACT_APP_GOOGLE_OAUTH_TOKENINFO_URL, {
        params: {
          access_token: accessToken
        }
      })
      .then(parseGoogleResponse)
      .then(apiServerPromise)
  },

  requestPasswordReset(email) {
    return agent.post(
      '/users/auth/password/reset',
      queryString.stringify({ email }),
      { headers: urlEncodedHeaders }
    )
  },

  resetPassword(code, email, password) {
    return agent.post(
      '/users/auth/password',
      queryString.stringify({
        code,
        email,
        password
      })
    )
  },

  internalResetPassword(currentPassword, newPassword) {
    return agent.put(
      '/users/auth/password',
      queryString.stringify({
        currentPassword,
        newPassword
      })
    )
  },

  findCompany(email) {
    return agent.get('/users/auth/emailCompanies', {
      params: { email },
      headers: urlEncodedHeaders
    })
  },

  startCompanyContext({ email, companyName }) {
    return agent
      .get('/users/auth/methods', {
        params: {
          email,
          companyName
        }
      })
      .then(({ data }) => data)
  },

  validateMFA({ email, token }) {
    return agent
      .post(
        '/users/auth/mfa/validate',
        queryString.stringify({ email, token }),
        {
          headers: urlEncodedHeaders
        }
      )
      .then(({ data }) => data)
  },

  generateGoogleUrl({ nextPathname }) {
    return createGoogleAuthenticationUrl(
      '/users/sso/google/login',
      nextPathname ? { nextPathname } : undefined
    )
  },

  generateFreeEditionGoogleUrl(params) {
    return createGoogleAuthenticationUrl(
      `/free/google${params && `?${queryString.stringify(params)}`}`
    )
  },

  generateSamlUrl({ companyName, nextPathname = '/' }) {
    const params = ['getCompanySession=true']

    if (companyName) {
      params.push('companyName=' + encodeURIComponent(`${companyName}`))
    }

    params.push('destination=' + encodeURIComponent(`${nextPathname}`))
    params.push(
      'acsHandler=' +
        encodeURIComponent(`${location.origin}/users/sso/saml/acs`)
    )
    return `${getBaseUrl('api')}/v1/users/auth/saml/login?${params.join('&')}`
  }
}

function createGoogleAuthenticationUrl(url, state) {
  const scope = REACT_APP_GOOGLE_OAUTH_SCOPE
  const clientId = REACT_APP_GOOGLE_OAUTH_CLIENT_ID
  const redirectUrl = `${location.origin}${url}`

  const params = [
    `scope=${encodeURIComponent(scope.replace(/,/g, ' '))}`,
    'redirect_uri=' + encodeURIComponent(redirectUrl),
    'response_type=token',
    `client_id=${clientId}`
  ]

  if (state) params.push(`state=${encodeURIComponent(JSON.stringify(state))}`)

  return `${REACT_APP_GOOGLE_OAUTH_URL}?${params.join('&')}`
}
